// Trends dashboard shell. Tab is part of the URL path (/trends/:tab);
// the time range lives in `?range=`. The shell only delegates rendering
// to per-tab components which own their own data fetches.
import { useEffect } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';

import TimeRangeControl from './TimeRangeControl';
import PulseTab from './tabs/PulseTab';
import InfrastructureTab from './tabs/InfrastructureTab';
import CveTab from './tabs/CveTab';
import SourcesTab from './tabs/SourcesTab';
import GeoTab from './tabs/GeoTab';
import TtpsTab from './tabs/TtpsTab';
import DiscoveryTab from './tabs/DiscoveryTab';
import SectorsTab from './tabs/SectorsTab';
import {
  TAB_DESCRIPTIONS,
  TAB_LABELS,
  TAB_ORDER,
  type TabKey,
} from './types';

import '../../styles/trends.css';

const TAB_COMPONENTS: Record<TabKey, () => JSX.Element> = {
  pulse: PulseTab,
  infrastructure: InfrastructureTab,
  cve: CveTab,
  sources: SourcesTab,
  geo: GeoTab,
  ttps: TtpsTab,
  discovery: DiscoveryTab,
  sectors: SectorsTab,
};

function isTab(value: string | undefined): value is TabKey {
  return !!value && (TAB_ORDER as string[]).includes(value);
}

export default function TrendsPage() {
  const { tab } = useParams<{ tab?: string }>();
  const resolved: TabKey | null = isTab(tab) ? tab : null;

  useEffect(() => {
    if (!resolved) return;
    const prev = document.title;
    document.title = `Trends · ${TAB_LABELS[resolved]} — MalpediaRAG`;
    return () => {
      document.title = prev;
    };
  }, [resolved]);

  if (!resolved) {
    return <Navigate to="/trends/pulse" replace />;
  }

  const TabComponent = TAB_COMPONENTS[resolved];

  return (
    <div className="trends-page">
      <header className="trends-header">
        <div className="trends-header-row">
          <div className="trends-titleblock">
            <h1>Trends</h1>
            <p className="trends-subtitle">{TAB_DESCRIPTIONS[resolved]}</p>
          </div>
          {/* Pulse owns its own dimension+range chips inside the tab body.
              The shared TimeRangeControl uses the v1 vocabulary which would
              conflict with Pulse's 6m/1y/2y/3y/5y/all chips. */}
          {resolved !== 'pulse' && <TimeRangeControl />}
        </div>
        <nav className="trends-tabbar" role="tablist" aria-label="Trends tabs">
          {TAB_ORDER.map((key) => (
            <NavLink
              key={key}
              to={`/trends/${key}`}
              role="tab"
              aria-selected={key === resolved}
              className={({ isActive }) =>
                `trends-tab${isActive ? ' active' : ''}`
              }
              end
            >
              {TAB_LABELS[key]}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="trends-tabpanel" role="tabpanel">
        <TabComponent />
      </main>
    </div>
  );
}
